import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetActionPlanAggregatedQuery } from '../../../../hooks/queries/useDashboardPublicQueries';
import { BaselineData, InterpretiveSummary, ActionPlanRecommendations } from '../../../../api/dashboardPublicApi';
import { LightBulbIcon, InformationSquareIcon, ArrowLeftIcon } from '@navikt/aksel-icons';
import { TeamExpectations } from '../TeamExpectations';
import { LanguageSelector } from '../../../../components/LanguageSelector';
import { useLanguageSynchronization } from '../../../../hooks/useLanguageSynchronization';
import { renderSimpleMarkdown, decodeHtmlEntities } from '../../../../utils/renderSimpleMarkdown';
import { capitalizeFirstLetter } from '../../../../utils/stringUtils';
import { RouterUtilsImpl } from '../../../../utils/RouterUtils';
import { NotReady } from '../../../../components/NotReady/NotReady';
import classes from './ActionPlanAggregated.module.css';

const routerUtils = new RouterUtilsImpl();
const MAX_INSIGHT_ITEMS = 4;

interface ActionPlanAggregatedProps {
  surveyUuid: string;
  onBackToResults?: () => void;
}

const clampPercentage = (value: number): number => {
  return Math.max(0, Math.min(100, Math.round(value)));
};

const LoadingView = () => {
  const { t } = useTranslation();
  return (
    <div className={classes.wrapper}>
      <p className={classes.loading}>
        {t('publicSurvey.actionPlan.aggregated.loading')}
      </p>
    </div>
  );
};

const BackToResultsButton = ({ surveyUuid }: { surveyUuid: string }) => {
  const { t, i18n } = useTranslation();
  const baseResultsUrl = routerUtils.getRoute('PublicSurveyResults', surveyUuid);

  // Preserve language parameter when navigating back
  const resultsUrl = i18n.language === 'en'
    ? `${baseResultsUrl}?language=EN`
    : baseResultsUrl;

  return (
    <Link
      to={resultsUrl}
      className={classes.backToResultsButton}
      aria-label={t('publicSurvey.actionPlan.aggregated.backToResults')}
    >
      <ArrowLeftIcon fontSize="1.25rem" aria-hidden="true" />
      <span>{t('publicSurvey.actionPlan.aggregated.backToResults')}</span>
    </Link>
  );
};

export const ActionPlanAggregated = ({
  surveyUuid,
  onBackToResults,
}: ActionPlanAggregatedProps) => {
  const { t, i18n } = useTranslation();
  const { handleLanguageChange } = useLanguageSynchronization();
  const {
    data: aggregated,
    isLoading,
    error,
  } = useGetActionPlanAggregatedQuery(surveyUuid);

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return <LoadingView />;
  }

  if (error || !aggregated) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.container}>
          <NotReady
            title={t('publicSurvey.actionPlan.aggregated.error.title')}
            description={t('publicSurvey.actionPlan.aggregated.error.description')}
          />
        </div>
      </div>
    );
  }

  const participationRate = clampPercentage(
    (aggregated.participantCount / aggregated.teamMemberCount) * 100
  );

  return (
    <>
      <div className={classes.navBar}>
        <BackToResultsButton surveyUuid={surveyUuid} />
        <LanguageSelector onLanguageChange={handleLanguageChange} />
      </div>

      <div className={classes.wrapper}>
        <div className={classes.container}>

        <ReportMasthead
          createdAt={aggregated.createdAt}
          formatDate={formatDate}
        />

        <ReportHeader
          categoryName={aggregated.categoryName}
        />

        {aggregated.baseline && (
          <BaselineInsight baseline={aggregated.baseline} />
        )}

        {aggregated.interpretiveSummary && (
          <InsightSummary summary={aggregated.interpretiveSummary} />
        )}

        <TeamExpectations
          commitments={aggregated.pairedItems
            .map(item => item.commitment)
            .filter((c): c is { statement: string } => Boolean(c?.statement))
            .map(c => ({
              statement: capitalizeFirstLetter(c.statement),
            }))}
          expectations={aggregated.pairedItems
            .map(item => item.expectation)
            .filter((e): e is { statement: string } => Boolean(e?.statement))
            .map(e => ({
              statement: capitalizeFirstLetter(e.statement),
            }))}
          maxItems={MAX_INSIGHT_ITEMS}
          showFooter={true}
        />

        <ActionRecommendations
          actionRecommendations={aggregated.actionRecommendations}
        />
        </div>
      </div>
    </>
  );
};

const ReportMasthead = ({
  createdAt,
  formatDate
}: {
  createdAt: Date;
  formatDate: (date: Date) => string;
}) => {
  const { t } = useTranslation();
  return (
    <div className={classes.masthead}>
      <span className={classes.mastheadBrand}>{t('publicSurvey.actionPlan.aggregated.masthead.brand')}</span>
      <div>{formatDate(createdAt)}</div>
    </div>
  );
};

const ReportHeader = ({ categoryName }: { categoryName: string }) => {
  const { t } = useTranslation();
  return (
    <div className={classes.header}>
      <div className={classes.eyebrow}>
        {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.header.eyebrow')}
      </div>
      <h1 className={classes.title}>{categoryName}</h1>
      <p className={classes.subtitle}>
        {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.header.subtitle')}
      </p>
    </div>
  );
};

const BaselineInsight = ({ baseline }: { baseline: BaselineData }) => {
  const { t } = useTranslation();

  const getTrendText = (trend: string, change: number) => {
    const absChange = Math.abs(change).toFixed(1);
    if (trend === 'up') {
      return t('publicSurvey.actionPlan.aggregated.aggregatedImproved.baseline.trendUp', { change: absChange });
    }
    if (trend === 'down') {
      return t('publicSurvey.actionPlan.aggregated.aggregatedImproved.baseline.trendDown', { change: absChange });
    }
    return t('publicSurvey.actionPlan.aggregated.aggregatedImproved.baseline.trendStable');
  };

  return (
    <div className={classes.baselineVisual}>
      <div className={classes.baselineHeader}>
        <h3 className={classes.baselineTitle}>
          {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.baseline.title')}
        </h3>
      </div>
      <div className={classes.baselineContent}>
        <div className={classes.baselineScores}>
          <div className={classes.baselineScore}>
            <div className={classes.baselineScoreLabel}>
              {t('publicSurvey.actionPlan.aggregated.baseline.previousLabel')}
            </div>
            <div className={classes.baselineScoreValue}>
              {baseline.previousScore.toFixed(1)}
            </div>
          </div>
          <div className={classes.baselineArrow}>→</div>
          <div className={classes.baselineScore}>
            <div className={classes.baselineScoreLabel}>
              {t('publicSurvey.actionPlan.aggregated.baseline.currentLabel')}
            </div>
            <div className={classes.baselineScoreValue}>
              {baseline.currentScore.toFixed(1)}
            </div>
          </div>
        </div>
        <div className={classes.baselineChange}>
          <p className={classes.baselineChangeText}>
            {getTrendText(baseline.scoreTrend, baseline.scoreChange)}
          </p>
        </div>
      </div>
    </div>
  );
};

const InsightSummary = ({ summary }: { summary?: InterpretiveSummary }) => {
  const { t } = useTranslation();

  if (!summary || !summary.paragraphs || summary.paragraphs.length === 0) {
    return null;
  }

  return (
    <div className={classes.interpretiveSummary}>
      <div className={classes.interpretiveSummaryHeader}>
        <span className={classes.interpretiveSummaryIcon}>
          <LightBulbIcon fontSize="1.125rem" />
        </span>
        <h2 className={classes.interpretiveSummaryTitle}>
          {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.insight.title')}
        </h2>
      </div>
      <div className={classes.interpretiveSummaryContent}>
        <ul className={classes.interpretiveSummaryList}>
          {summary.paragraphs.slice(0, 4).map((paragraph, index) => {
            const text = typeof paragraph === 'string' ? paragraph : String(paragraph);
            return (
              <li key={index} className={classes.interpretiveSummaryListItem}>
                {text}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const ActionRecommendations = ({
  actionRecommendations
}: {
  actionRecommendations?: ActionPlanRecommendations;
}) => {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(0);

  const toggleAccordion = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const parsePracticeTips = (text: string): string[] => {
    const cleanedText = text.replace(/^Slik gjør du det i praksis:\s*/i, '');

    const tips: string[] = [];
    const regex = /\*\*([^*]+)\*\*/g;
    let lastIndex = 0;
    let match;
    let currentTip = '';

    while ((match = regex.exec(cleanedText)) !== null) {
      const textBefore = cleanedText.substring(lastIndex, match.index);

      if (currentTip) {
        currentTip += textBefore;
        if (currentTip.trim()) {
          tips.push(currentTip.trim());
        }
      }

      currentTip = match[0];
      lastIndex = match.index + match[0].length;
    }

    const remaining = cleanedText.substring(lastIndex);
    if (currentTip || remaining.trim()) {
      currentTip += remaining;
      if (currentTip.trim()) {
        tips.push(currentTip.trim());
      }
    }

    return tips.filter(tip => tip.length > 0 && tip !== '**' && tip !== '****');
  };

  if (actionRecommendations?.recommendations && Array.isArray(actionRecommendations.recommendations) && actionRecommendations.recommendations.length > 0) {
    return (
      <div className={classes.howToStart}>
        <h2 className={classes.sectionTitle}>
          {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.title')}
        </h2>
        <div className={classes.aiRecommendations}>
          {actionRecommendations.recommendations.map((rec, index) => {
            const isExpanded = expandedIndex === index;
            const barrierText = rec.content[0] || '';
            const practiceText = rec.content[1] || '';
            const practiceTips = parsePracticeTips(practiceText);

            return (
              <div key={rec.number} className={classes.recommendationAccordion}>
                <button
                  className={classes.recommendationAccordionButton}
                  onClick={() => toggleAccordion(index)}
                  aria-expanded={isExpanded}
                >
                  <span className={classes.recommendationAccordionTitle}>
                    {rec.number}. {decodeHtmlEntities(rec.title)}
                  </span>
                  <span className={classes.recommendationAccordionIcon}>
                    {isExpanded ? '−' : '+'}
                  </span>
                </button>

                {isExpanded && (
                  <div className={classes.recommendationAccordionContent}>
                    <div className={classes.recommendationBarrier}>
                      {renderSimpleMarkdown(barrierText)}
                    </div>

                    {practiceTips.length > 0 && (
                      <ul className={classes.recommendationTipsList}>
                        {practiceTips.map((tip, tipIdx) => (
                          <li key={tipIdx} className={classes.recommendationTip}>
                            {renderSimpleMarkdown(tip)}
                          </li>
                        ))}
                      </ul>
                    )}

                    {rec.example && (
                      <div className={classes.recommendationExample}>
                        <strong>
                          {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.concreteExample')}
                        </strong>
                        {' '}
                        {decodeHtmlEntities(rec.example)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.howToStart}>
      <h2 className={classes.sectionTitle}>
        {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.title')}
      </h2>
      <p className={classes.howToStartIntro}>
        {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.intro')}
      </p>

      <div className={classes.howToStartGrid}>
        <div className={classes.howToStartCard}>
          <h3 className={classes.howToStartCardTitle}>
            {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card1.title')}
          </h3>
          <p className={classes.howToStartCardText}>
            {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card1.text')}
          </p>
          <div className={classes.howToStartExample}>
            <div className={classes.howToStartExampleLabel}>
              {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.exampleLabel')}
            </div>
            <p className={classes.howToStartExampleText}>
              {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card1.example')}
            </p>
          </div>
        </div>

        <div className={classes.howToStartCard}>
          <h3 className={classes.howToStartCardTitle}>
            {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card2.title')}
          </h3>
          <p className={classes.howToStartCardText}>
            {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card2.text')}
          </p>
          <div className={classes.howToStartExample}>
            <div className={classes.howToStartExampleLabel}>
              {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.exampleLabel')}
            </div>
            <p className={classes.howToStartExampleText}>
              {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card2.example')}
            </p>
          </div>
        </div>

        <div className={classes.howToStartCard}>
          <h3 className={classes.howToStartCardTitle}>
            {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card3.title')}
          </h3>
          <p className={classes.howToStartCardText}>
            {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card3.text')}
          </p>
          <div className={classes.howToStartExample}>
            <div className={classes.howToStartExampleLabel}>
              {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.exampleLabel')}
            </div>
            <p className={classes.howToStartExampleText}>
              {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.card3.example')}
            </p>
          </div>
        </div>
      </div>

      <div className={classes.safetyReminder}>
        <span className={classes.safetyReminderIcon}>
          <InformationSquareIcon fontSize="1.25rem" />
        </span>
        <div>
          <h4 className={classes.safetyReminderTitle}>
            {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.reminder.title')}
          </h4>
          <p className={classes.safetyReminderText}>
            {t('publicSurvey.actionPlan.aggregated.aggregatedImproved.actions.reminder.text')}
          </p>
        </div>
      </div>
    </div>
  );
};
